import { LOCAL_STORAGE_KEYS, STORAGE_KEYS_PREFIX } from '@/constants/storage';

export function hasWindow() {
	return typeof window !== 'undefined';
}

export interface StorageClient<KeyType extends string> {
	setItem: (key: KeyType, value: string) => void;

	getItem: (key: KeyType) => string | null | undefined;

	removeItem: (key: KeyType) => void | undefined;
}

export function getLocalStorageClient<KeyType extends string>(
	prefix: string
): StorageClient<KeyType> {
	function setItem(key: KeyType, value: string) {
		hasWindow() && localStorage.setItem(`${prefix}.${key}`, value);
	}

	function getItem(key: KeyType) {
		return hasWindow() ? localStorage.getItem(`${prefix}.${key}`) : undefined;
	}

	function removeItem(key: KeyType) {
		hasWindow() && localStorage.removeItem(`${prefix}.${key}`);
	}

	return { setItem, getItem, removeItem };
}

export const localStorageClient = getLocalStorageClient<LOCAL_STORAGE_KEYS>(STORAGE_KEYS_PREFIX);

/**
 * Defers a callback to run after the browser has painted, yielding the main thread
 * to avoid blocking Interaction to Next Paint (INP).
 *
 * Uses scheduler.postTask (background priority) when available, falls back to setTimeout.
 */
export function yieldToMain(callback: () => void) {
	if (
		hasWindow() &&
		'scheduler' in window &&
		typeof (window.scheduler as { postTask?: unknown }).postTask === 'function'
	) {
		(
			window.scheduler as { postTask: (cb: () => void, opts: { priority: string }) => void }
		).postTask(callback, { priority: 'background' });
	} else {
		setTimeout(callback, 0);
	}
}
